import { jsPDF } from 'jspdf';

const BRAND_BLUE = [37, 99, 235];
const BRAND_PURPLE = [124, 58, 237];
const DARK = [17, 24, 39];
const GRAY = [107, 114, 128];
const LIGHT_GRAY = [243, 244, 246];
const WHITE = [255, 255, 255];
const ACCENT = [59, 130, 246];

const steps = {
  en: [
    {
      id: 1, title: 'Your Vision',
      description: 'Everything is created twice: first in your mind, then in reality.',
      quote: '"Vision is the art of seeing things invisible." — Jonathan Swift',
      content: 'Your vision is your North Star. Before anything exists in the real world, it must first be conceived as a thought in your mind. Imagine what your life will look like in 5 years, what successes you will have achieved, and what business you will have built.',
      tool: 'Vision Tool',
      prompt: 'You meet an old school friend in 5 years and they ask: "How are you and how\'s your life going?" You answer: "It\'s going great, it couldn\'t be better, because..."',
      lines: 8,
      actions: ['Clearly articulate your 5-year future scenario', 'Identify the core achievements that make it "perfect"']
    },
    {
      id: 2, title: 'Your Knowledge',
      description: 'Understand your domain and leverage your unique expertise.',
      quote: '"Knowledge is power." — Francis Bacon',
      content: 'Success starts with what you know. Map out your existing knowledge and identify where you need to learn more. Your expertise is the foundation of your business.',
      tool: 'Knowledge Mapping',
      prompt: 'List your top areas of expertise, skills, and industry knowledge. For each, note your level of mastery and how it applies to your business idea.',
      lines: 8,
      actions: ['List your top 5 technical skills', 'Identify 3 gaps in your market knowledge']
    },
    {
      id: 3, title: 'Your Idea',
      description: 'Brainstorm and evaluate potential solutions to the problem you identified.',
      quote: '"The best way to have a good idea is to have a lot of ideas." — Linus Pauling',
      content: 'Your idea must solve a specific problem for a defined target group. Test it for success by answering the key questions below.',
      tool: 'Idea Validation Tool',
      prompt: 'Answer each question carefully:\n• What specific problem does your idea solve?\n• For whom (target group) do you solve this?\n• What need does your idea fulfill?\n• Why are YOU suitable to solve this?\n• What is your product name and slogan?',
      lines: 10,
      actions: ['Generate 20 different ways to solve the problem', 'Group similar ideas into core concepts']
    },
    {
      id: 4, title: 'Your Target Audience',
      description: 'Deeply understand who you are building for and what they really need.',
      quote: '"Don\'t find customers for your products, find products for your customers." — Seth Godin',
      content: 'Building for everyone is building for no one. A precise understanding of your target audience is crucial. Put yourself in your customers\' shoes.',
      tool: 'Customer Avatar Tool',
      prompt: 'Create your customer avatar:\n• Name & Demographics (Age, Job, Location):\n• Motivations & Goals:\n• Challenges & Pain Points:\n• What does a typical day look like for them?',
      lines: 10,
      actions: ['Create a detailed persona of your early adopter', 'Interview 5 potential users']
    },
    {
      id: 5, title: 'Find Your Niche',
      description: 'Identify the specific market segment where you can dominate.',
      quote: '"Be a big fish in a small pond." — Proverb',
      content: 'A niche allows you to become the authority quickly. Using the Blue Ocean Strategy, find the uncontested market space where the competition becomes irrelevant.',
      tool: 'Blue Ocean Strategy (ERRC Grid)',
      prompt: 'Complete the Four Actions Framework:\n• ELIMINATE – What industry factors should be eliminated?\n• REDUCE – What should be reduced below industry standard?\n• RAISE – What should be raised above industry standard?\n• CREATE – What new factors should be created?',
      lines: 10,
      actions: ['Map your value curve against competitors', 'Identify 3 over-served market areas']
    },
    {
      id: 6, title: 'Create Relevance in Your Niche',
      description: 'Ensure your solution is exactly what your niche is looking for.',
      quote: '"Relevance is the new currency." — Anonymous',
      content: 'Resonance is key. Refine your positioning to ensure it hits home with your target group. Align your messaging with their core values.',
      tool: 'Relevance & Messaging Tool',
      prompt: 'Define your core messaging:\n• How do you describe your solution to your niche?\n• What values does your target group care about most?\n• What makes your solution uniquely relevant to them?',
      lines: 8,
      actions: ['Draft your core value proposition', 'Test messaging with your target audience']
    },
    {
      id: 7, title: 'Formulate Your Offer',
      description: 'Package your solution into a compelling product or service.',
      quote: '"Make them an offer they can\'t refuse." — Mario Puzo',
      content: 'Your offer is more than just a product. Use the Perfect Offer Formula and the Emotional Journey to craft an irresistible offer.',
      tool: 'Offer Formulation Tool',
      prompt: 'The Perfect Offer Formula:\n"[PRODUCT] helps [TARGET GROUP] achieve [GOAL] without [OBSTACLE]."\n\nFill in your formula:\n\nEmotional Journey:\n• BEFORE (without your product): How do they feel?\n• AFTER (with your product): How do they feel?',
      lines: 10,
      actions: ['Define your MVP feature set', 'Set your initial price point']
    },
    {
      id: 8, title: 'Build Trust Through Content',
      description: 'Create a content strategy that establishes you as an authority.',
      quote: '"Content builds relationships. Relationships are built on trust. Trust drives revenue." — Andrew Davis',
      content: 'Content becomes trust when it is refined, and this happens through relevance to the target audience. Relevant content is created through: Visibility over time; insignia; factual information; cooperation & networking.',
      tool: 'Trust & Content Strategy Tool',
      prompt: 'Plan your trust-building content strategy:\n• Visibility Strategy (How will you show up consistently over time?):\n• Insignia (What proof of expertise will you share?):\n• Factual Information (What facts/data support your authority?):\n• Cooperation & Networking (Who will you partner with?):',
      lines: 10,
      actions: ['Plan 4 weeks of educational content', 'Identify 2 case study opportunities']
    },
    {
      id: 9, title: 'Co-Create Your MVP',
      description: 'Get a functional version of your idea into the hands of real users.',
      quote: '"If you are not embarrassed by the first version of your product, you\'ve launched too late." — Reid Hoffman',
      content: 'Rapid testing is essential for the feedback loop. Focus on the core functionality that delivers value. Don\'t build in isolation.',
      tool: 'MVP Scope & Roadmap',
      prompt: 'Define your MVP:\n• Core Feature #1 (must-have):\n• Core Feature #2 (must-have):\n• Nice-to-have (can wait):\n• How will you collect user feedback?\n• What does "success" look like for your MVP launch?',
      lines: 10,
      actions: ['Build the core feature of your MVP', 'Set up a feedback system']
    },
    {
      id: 10, title: 'Your Launch Plan',
      description: 'Prepare everything for a successful go-to-market execution.',
      quote: '"Execution is everything." — John Doerr',
      content: 'A great product needs a great launch. Plan the steps to ensure maximum impact and prepare everything for a successful go-to-market execution.',
      tool: 'Launch Planner',
      prompt: 'Complete your launch checklist:\n• Launch Date:\n• Target #1 (first users/customers):\n• Key marketing channels:\n• Pre-launch buzz strategy:\n• KPIs to track in week 1:\n• Who will you celebrate with?',
      lines: 10,
      actions: ['Set your launch date', 'Create a pre-launch buzz campaign']
    }
  ],
  de: [
    {
      id: 1, title: 'Deine Vision',
      description: 'Alles wird zweimal erschaffen: erst in deinem Kopf, dann in der Realität.',
      quote: '"Vision ist die Kunst, Unsichtbares zu sehen." — Jonathan Swift',
      content: 'Deine Vision ist dein Nordstern. Bevor etwas in der realen Welt existiert, muss es zuerst als Gedanke in deinem Kopf entstehen. Stelle dir vor, wie dein Leben in 5 Jahren aussieht.',
      tool: 'Visions-Tool',
      prompt: 'Du triffst in 5 Jahren einen alten Schulfreund und er fragt: "Wie geht es dir und wie läuft dein Leben?" Du antwortest: "Es läuft super, es könnte nicht besser sein, weil..."',
      lines: 8,
      actions: ['Formuliere dein 5-Jahres-Zukunftsszenario', 'Identifiziere die Erfolge, die es "perfekt" machen']
    },
    {
      id: 2, title: 'Dein Wissen',
      description: 'Verstehe deine Wissens-Domäne und nutze dein einzigartiges Fachwissen.',
      quote: '"Wissen ist Macht." — Francis Bacon',
      content: 'Erfolg beginnt mit dem, was du weißt. Erfasse dein vorhandenes Wissen und identifiziere Lernfelder. Deine Expertise ist das Fundament deines Business.',
      tool: 'Wissens-Mapping',
      prompt: 'Liste deine wichtigsten Wissensgebiete, Fähigkeiten und Branchen-Know-how auf. Notiere für jedes deinen Kompetenzgrad und die Relevanz für deine Geschäftsidee.',
      lines: 8,
      actions: ['Liste deine Top 5 Fähigkeiten auf', 'Identifiziere 3 Wissenslücken']
    },
    {
      id: 3, title: 'Deine Idee',
      description: 'Brainstorme und bewerte potenzielle Lösungen für dein Problem.',
      quote: '"Der beste Weg, eine gute Idee zu haben, ist, viele Ideen zu haben." — Linus Pauling',
      content: 'Deine Idee muss ein spezifisches Problem für eine definierte Zielgruppe lösen. Teste sie vorab auf Erfolgschancen.',
      tool: 'Ideen-Validierungs-Tool',
      prompt: 'Beantworte jede Frage sorgfältig:\n• Welches spezifische Problem löst deine Idee?\n• Für wen (Zielgruppe) löst du dieses?\n• Welches Bedürfnis erfüllt deine Idee?\n• Warum bist du geeignet, dieses Problem zu lösen?\n• Wie soll dein Produkt heißen und welcher Slogan wird genutzt?',
      lines: 10,
      actions: ['Generiere 20 verschiedene Lösungsansätze', 'Gruppiere Ideen in Kernkonzepte']
    },
    {
      id: 4, title: 'Deine Zielgruppe',
      description: 'Verstehe tiefgreifend, für wen du baust und was diese Menschen wirklich brauchen.',
      quote: '"Finde nicht Kunden für deine Produkte, finde Produkte für deine Kunden." — Seth Godin',
      content: 'Wer für alle baut, baut für niemanden. Ein präzises Verständnis deiner Zielgruppe ist entscheidend.',
      tool: 'Kunden-Avatar-Tool',
      prompt: 'Erstelle deinen Kunden-Avatar:\n• Name & Demografie (Alter, Beruf, Ort):\n• Motivationen & Ziele:\n• Herausforderungen & Sorgen:\n• Wie sieht ein typischer Tag für sie aus?',
      lines: 10,
      actions: ['Erstelle ein detailliertes Profil deines Early Adopters', 'Führe 5 Interviews mit potenziellen Nutzern']
    },
    {
      id: 5, title: 'Finde deine Nische',
      description: 'Identifiziere das Marktsegment, in dem du dominieren kannst.',
      quote: '"Sei ein großer Fisch in einem kleinen Teich." — Sprichwort',
      content: 'Eine Nische ermöglicht es dir, schnell zur Autorität zu werden. Finde mit der Blue Ocean Strategie deinen unangetasteten Markt.',
      tool: 'Blue Ocean Strategie (ERRC-Raster)',
      prompt: 'Fülle das Vier-Aktionen-Format aus:\n• ELIMINIEREN – Welche Branchenfaktoren sollten eliminiert werden?\n• REDUZIEREN – Was sollte unter den Branchenstandard reduziert werden?\n• STEIGERN – Was sollte über den Branchenstandard gesteigert werden?\n• KREIEREN – Welche neuen Faktoren sollten geschaffen werden?',
      lines: 10,
      actions: ['Zeichne deine Nutzenkurve im Vergleich zum Wettbewerb', 'Identifiziere 3 überversorgte Bereiche']
    },
    {
      id: 6, title: 'Schaffe Relevanz in deiner Nische',
      description: 'Stelle sicher, dass deine Lösung genau das ist, wonach deine Nische sucht.',
      quote: '"Relevanz ist die neue Währung." — Anonym',
      content: 'Resonanz ist der Schlüssel. Verfeinere deine Positionierung. Richte deine Botschaften an den Kernwerten deiner Zielgruppe aus.',
      tool: 'Relevanz & Messaging Tool',
      prompt: 'Definiere deine Kern-Botschaft:\n• Wie beschreibst du deine Lösung für deine Nische?\n• Welche Werte sind deiner Zielgruppe am wichtigsten?\n• Was macht deine Lösung einzigartig relevant für sie?',
      lines: 8,
      actions: ['Entwirf dein Kern-Wertversprechen', 'Teste deine Botschaften mit der Zielgruppe']
    },
    {
      id: 7, title: 'Formuliere dein Angebot',
      description: 'Verpacke deine Lösung in ein unwiderstehliches Produkt oder eine Dienstleistung.',
      quote: '"Mach ihnen ein Angebot, das sie nicht ablehnen können." — Mario Puzo',
      content: 'Dein Angebot ist mehr als nur ein Produkt. Nutze die perfekte Angebots-Formel und die emotionale Reise.',
      tool: 'Angebots-Tool',
      prompt: 'Die perfekte Angebots-Formel:\n"[PRODUKT] hilft [ZIELGRUPPE], [ZIEL] zu erreichen, ohne [HÜRDE]."\n\nFülle deine Formel aus:\n\nEmotionale Reise:\n• VORHER (ohne dein Produkt): Wie fühlen sie sich?\n• NACHHER (mit deinem Produkt): Wie fühlen sie sich?',
      lines: 10,
      actions: ['Definiere den Funktionsumfang deines MVPs', 'Lege deinen ersten Preis fest']
    },
    {
      id: 8, title: 'Baue Vertrauen durch Content auf',
      description: 'Erstelle eine Content-Strategie, die dich als Autorität etabliert.',
      quote: '"Content baut Beziehungen auf. Beziehungen bauen auf Vertrauen. Vertrauen generiert Umsatz." — Andrew Davis',
      content: 'Content wird zu Vertrauen, wenn er veredelt wird – durch Relevanz für die Zielgruppe. Relevanter Content entsteht durch: Sichtbarkeit über Zeit; Insignien; Sachinformationen; Kooperation & Networking.',
      tool: 'Vertrauen & Content-Strategie Tool',
      prompt: 'Plane deine Vertrauens-Content-Strategie:\n• Sichtbarkeit (Wie zeigst du dich konsistent über Zeit?):\n• Insignien (Welche Expertise-Beweise teilst du?):\n• Sachinformationen (Welche Fakten/Daten stützen deine Autorität?):\n• Kooperation & Networking (Mit wem kooperierst du?):',
      lines: 10,
      actions: ['Plane Content für 4 Wochen', 'Identifiziere Möglichkeiten für Fallstudien']
    },
    {
      id: 9, title: 'Co-Create MVP',
      description: 'Bringe eine funktionale Version deiner Idee in die Hände echter Nutzer.',
      quote: '"Wenn dir die erste Version nicht peinlich ist, hast du zu spät gelauncht." — Reid Hoffman',
      content: 'Schnelles Testen ist für den Feedback-Loop unerlässlich. Konzentriere dich auf die Kernfunktionalität.',
      tool: 'MVP-Umfang & Roadmap',
      prompt: 'Definiere dein MVP:\n• Kern-Feature #1 (Muss):\n• Kern-Feature #2 (Muss):\n• Nice-to-have (kann warten):\n• Wie sammelst du Nutzer-Feedback?\n• Was bedeutet "Erfolg" für deinen MVP-Launch?',
      lines: 10,
      actions: ['Baue das Kern-Feature deines MVPs', 'Richte ein Feedback-System ein']
    },
    {
      id: 10, title: 'Dein Launch-Plan',
      description: 'Bereite alles für eine erfolgreiche Markteinführung vor.',
      quote: '"Ausführung ist alles." — John Doerr',
      content: 'Ein tolles Produkt braucht einen tollen Launch. Plane jeden Schritt für maximale Wirkung.',
      tool: 'Launch-Planer',
      prompt: 'Vervollständige deine Launch-Checkliste:\n• Launch-Datum:\n• Ziel #1 (erste Nutzer/Kunden):\n• Wichtigste Marketing-Kanäle:\n• Pre-Launch-Strategie:\n• KPIs für Woche 1:\n• Mit wem feierst du?',
      lines: 10,
      actions: ['Lege dein Launch-Datum fest', 'Erstelle eine Pre-Launch-Kampagne']
    }
  ]
};

const drawGradientRect = (doc, x, y, w, h) => {
  const steps = 30;
  for (let i = 0; i < steps; i++) {
    const ratio = i / steps;
    const r = Math.round(BRAND_BLUE[0] + (BRAND_PURPLE[0] - BRAND_BLUE[0]) * ratio);
    const g = Math.round(BRAND_BLUE[1] + (BRAND_PURPLE[1] - BRAND_BLUE[1]) * ratio);
    const b = Math.round(BRAND_BLUE[2] + (BRAND_PURPLE[2] - BRAND_BLUE[2]) * ratio);
    doc.setFillColor(r, g, b);
    doc.rect(x + (w / steps) * i, y, w / steps + 0.5, h, 'F');
  }
};

const wrapText = (doc, text, x, y, maxWidth, lineHeight) => {
  const lines = doc.splitTextToSize(text, maxWidth);
  lines.forEach((line, i) => {
    doc.text(line, x, y + i * lineHeight);
  });
  return y + lines.length * lineHeight;
};

const addCoverPage = (doc, lang) => {
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();

  // Background
  doc.setFillColor(...LIGHT_GRAY);
  doc.rect(0, 0, pw, ph, 'F');

  // Top gradient bar
  drawGradientRect(doc, 0, 0, pw, 60);

  // Decorative circles
  doc.setFillColor(255, 255, 255, 0.05);
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(0.3);
  doc.circle(pw - 30, 30, 50, 'S');
  doc.circle(pw - 30, 30, 35, 'S');
  doc.circle(30, ph - 30, 40, 'S');

  // Logo / Brand
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...WHITE);
  doc.text('STUP-ON', 20, 25);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('erfolgsspur.stup-on.de', 20, 33);

  // Main title area
  const titleY = 100;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(42);
  doc.setTextColor(...DARK);
  doc.text(lang === 'de' ? 'DEIN' : 'YOUR', 20, titleY);
  doc.setFontSize(42);
  doc.setTextColor(...BRAND_BLUE[0], ...BRAND_BLUE.slice(1));
  doc.setTextColor(37, 99, 235);
  doc.text(lang === 'de' ? '10-SCHRITTE' : '10-STEP', 20, titleY + 18);
  doc.setTextColor(...DARK);
  doc.text(lang === 'de' ? 'WORKBOOK' : 'WORKBOOK', 20, titleY + 36);

  // Subtitle line
  doc.setDrawColor(...BRAND_BLUE);
  doc.setLineWidth(1.5);
  doc.line(20, titleY + 44, 80, titleY + 44);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(13);
  doc.setTextColor(...GRAY);
  const sub = lang === 'de'
    ? 'Von der Vision zum erfolgreichen Launch'
    : 'From Vision to a Successful Launch';
  doc.text(sub, 20, titleY + 58);

  // Step badges row
  const badgeY = titleY + 80;
  for (let i = 1; i <= 10; i++) {
    const bx = 20 + (i - 1) * 17;
    drawGradientRect(doc, bx, badgeY, 14, 14);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...WHITE);
    doc.text(String(i), bx + 7, badgeY + 9, { align: 'center' });
  }
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  doc.text(lang === 'de' ? '10 Schritte · 10 Werkzeuge · 1 Ziel' : '10 Steps · 10 Tools · 1 Goal', 20, badgeY + 22);

  // Feature boxes
  const features = lang === 'de'
    ? [['📋', 'Strukturierter Prozess'], ['🎯', 'Interaktive Tools'], ['🚀', 'Launch-Bereit']]
    : [['📋', 'Structured Process'], ['🎯', 'Interactive Tools'], ['🚀', 'Launch-Ready']];

  const boxY = badgeY + 38;
  features.forEach(([icon, label], i) => {
    const bx = 20 + i * 60;
    doc.setFillColor(...WHITE);
    doc.roundedRect(bx, boxY, 55, 22, 3, 3, 'F');
    doc.setDrawColor(229, 231, 235);
    doc.setLineWidth(0.3);
    doc.roundedRect(bx, boxY, 55, 22, 3, 3, 'S');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...DARK);
    doc.text(`${icon}  ${label}`, bx + 5, boxY + 13);
  });

  // Bottom band
  drawGradientRect(doc, 0, ph - 28, pw, 28);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...WHITE);
  const year = new Date().getFullYear();
  doc.text(`© ${year} Stup-On · erfolgsspur.stup-on.de`, pw / 2, ph - 12, { align: 'center' });
};

const addInstructionsPage = (doc, lang) => {
  doc.addPage();
  const pw = doc.internal.pageSize.getWidth();

  drawGradientRect(doc, 0, 0, pw, 18);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...WHITE);
  doc.text(lang === 'de' ? 'WIE DU DIESES WORKBOOK NUTZT' : 'HOW TO USE THIS WORKBOOK', pw / 2, 12, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(...DARK);
  doc.text(lang === 'de' ? 'Dein Leitfaden' : 'Your Guide', 20, 42);

  doc.setDrawColor(...BRAND_BLUE);
  doc.setLineWidth(1.5);
  doc.line(20, 46, 60, 46);

  const intro = lang === 'de'
    ? 'Dieses Workbook begleitet dich durch alle 10 Schritte der Stup-On-Methodik. Jeder Schritt enthält:\n\n• Eine klare Beschreibung des Schrittes\n• Ein inspirierendes Zitat\n• Den Kerninhalt und die Theorie\n• Ein praktisches Tool mit konkreten Fragen\n• Deine persönlichen Aufgaben (Action Items)\n• Platz zum Schreiben und Reflektieren\n\nNimm dir Zeit für jeden Schritt. Die Qualität deiner Antworten bestimmt die Qualität deines Ergebnisses. Sei ehrlich mit dir selbst – das ist die Grundlage für echten Erfolg.'
    : 'This workbook guides you through all 10 steps of the Stup-On methodology. Each step contains:\n\n• A clear description of the step\n• An inspiring quote\n• The core content and theory\n• A practical tool with concrete questions\n• Your personal action items\n• Space for writing and reflection\n\nTake your time with each step. The quality of your answers determines the quality of your results. Be honest with yourself – this is the foundation for real success.';

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...GRAY);
  wrapText(doc, intro, 20, 60, 170, 6);

  // Tips box
  const tipY = 175;
  doc.setFillColor(239, 246, 255);
  doc.roundedRect(20, tipY, 170, 55, 4, 4, 'F');
  doc.setDrawColor(147, 197, 253);
  doc.setLineWidth(0.5);
  doc.roundedRect(20, tipY, 170, 55, 4, 4, 'S');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(37, 99, 235);
  doc.text(lang === 'de' ? '💡 Tipps für die beste Nutzung' : '💡 Tips for Best Results', 28, tipY + 12);

  const tips = lang === 'de'
    ? ['Drucke dieses Workbook aus und fülle es handschriftlich aus.', 'Bearbeite jeden Schritt vollständig, bevor du weitermachst.', 'Kehre regelmäßig zurück und überarbeite deine Antworten.', 'Nutze die digitalen Tools auf erfolgsspur.stup-on.de ergänzend.']
    : ['Print this workbook and fill it in by hand.', 'Complete each step fully before moving on.', 'Return regularly and revise your answers.', 'Use the digital tools at erfolgsspur.stup-on.de alongside this workbook.'];

  tips.forEach((tip, i) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...DARK);
    doc.text(`${i + 1}. ${tip}`, 28, tipY + 22 + i * 8);
  });
};

const addStepPage = (doc, step, stepIndex, lang) => {
  doc.addPage();
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();

  // Header gradient
  drawGradientRect(doc, 0, 0, pw, 22);

  // Step number badge in header
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...WHITE);
  doc.text(
    lang === 'de' ? `SCHRITT ${step.id} VON 10` : `STEP ${step.id} OF 10`,
    20, 14
  );
  doc.text('STUP-ON WORKBOOK', pw - 20, 14, { align: 'right' });

  // Large step number watermark
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(120);
  doc.setTextColor(243, 244, 246);
  doc.text(String(step.id).padStart(2, '0'), pw - 15, 85, { align: 'right' });

  // Step title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(...DARK);
  doc.text(step.title.toUpperCase(), 20, 42);

  // Blue underline
  doc.setDrawColor(...BRAND_BLUE);
  doc.setLineWidth(2);
  doc.line(20, 47, 20 + Math.min(step.title.length * 5.5, 150), 47);

  // Description
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(10);
  doc.setTextColor(...GRAY);
  let y = wrapText(doc, step.description, 20, 58, 150, 6);

  // Quote box
  y += 4;
  doc.setFillColor(239, 246, 255);
  const quoteLines = doc.splitTextToSize(step.quote, 155);
  const quoteH = quoteLines.length * 6 + 14;
  doc.roundedRect(20, y, 170, quoteH, 3, 3, 'F');
  doc.setDrawColor(37, 99, 235);
  doc.setLineWidth(2);
  doc.line(20, y, 20, y + quoteH);
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  doc.setTextColor(37, 99, 235);
  quoteLines.forEach((line, i) => doc.text(line, 27, y + 9 + i * 6));
  y += quoteH + 8;

  // Content section
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...DARK);
  doc.text(lang === 'de' ? '📖 KERNINHALT' : '📖 CORE CONTENT', 20, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  y = wrapText(doc, step.content, 20, y, 170, 5.5);
  y += 8;

  // Tool section
  doc.setFillColor(249, 250, 251);
  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.5);

  // Estimate tool box height
  const toolPromptLines = doc.splitTextToSize(step.prompt, 160);
  const toolBoxH = toolPromptLines.length * 5.5 + 20;
  doc.roundedRect(20, y, 170, toolBoxH, 3, 3, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...BRAND_BLUE[0], ...BRAND_BLUE.slice(1));
  doc.setTextColor(37, 99, 235);
  doc.text(`🛠  ${step.tool.toUpperCase()}`, 27, y + 10);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...DARK);
  toolPromptLines.forEach((line, i) => doc.text(line, 27, y + 18 + i * 5.5));
  y += toolBoxH + 8;

  // Writing lines
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...DARK);
  doc.text(lang === 'de' ? '✏️  DEINE ANTWORTEN:' : '✏️  YOUR ANSWERS:', 20, y);
  y += 7;

  const lineCount = step.lines || 8;
  for (let i = 0; i < lineCount; i++) {
    if (y + 8 > ph - 35) break;
    doc.setDrawColor(209, 213, 219);
    doc.setLineWidth(0.3);
    doc.line(20, y + 6, pw - 20, y + 6);
    y += 8;
  }
  y += 4;

  // Action items
  if (y + 30 < ph - 20 && step.actions?.length) {
    doc.setFillColor(240, 253, 244);
    doc.setDrawColor(134, 239, 172);
    doc.setLineWidth(0.5);
    const actH = step.actions.length * 9 + 16;
    doc.roundedRect(20, y, 170, actH, 3, 3, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(22, 163, 74);
    doc.text(lang === 'de' ? '✅  DEINE AUFGABEN (ACTION ITEMS)' : '✅  YOUR ACTION ITEMS', 27, y + 10);
    step.actions.forEach((action, i) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(...DARK);
      doc.text(`${i + 1}.  ${action}`, 27, y + 18 + i * 9);
      // Checkbox
      doc.setDrawColor(134, 239, 172);
      doc.setLineWidth(0.5);
      doc.rect(pw - 28, y + 13 + i * 9, 6, 6);
    });
    y += actH + 6;
  }

  // Footer
  doc.setFillColor(...LIGHT_GRAY);
  doc.rect(0, ph - 14, pw, 14, 'F');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...GRAY);
  doc.text(`Stup-On · erfolgsspur.stup-on.de`, 20, ph - 5);
  doc.text(`${step.id} / 10`, pw - 20, ph - 5, { align: 'right' });
};

const addNotesPage = (doc, lang) => {
  doc.addPage();
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();

  drawGradientRect(doc, 0, 0, pw, 22);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...WHITE);
  doc.text(lang === 'de' ? 'NOTIZEN & REFLEXIONEN' : 'NOTES & REFLECTIONS', pw / 2, 14, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(...DARK);
  doc.text(lang === 'de' ? 'Meine Notizen' : 'My Notes', 20, 40);

  doc.setDrawColor(...BRAND_BLUE);
  doc.setLineWidth(1.5);
  doc.line(20, 44, 70, 44);

  let y = 55;
  while (y < ph - 20) {
    doc.setDrawColor(209, 213, 219);
    doc.setLineWidth(0.3);
    doc.line(20, y, pw - 20, y);
    y += 9;
  }

  doc.setFillColor(...LIGHT_GRAY);
  doc.rect(0, ph - 14, pw, 14, 'F');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...GRAY);
  doc.text('Stup-On · erfolgsspur.stup-on.de', pw / 2, ph - 5, { align: 'center' });
};

export const generateWorkbookPDF = (lang = 'en') => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const stepList = steps[lang] || steps.en;

  addCoverPage(doc, lang);
  addInstructionsPage(doc, lang);
  stepList.forEach((step, i) => addStepPage(doc, step, i, lang));
  addNotesPage(doc, lang);

  const filename = lang === 'de' ? 'Stup-On_10-Schritte-Workbook.pdf' : 'Stup-On_10-Step-Workbook.pdf';
  doc.save(filename);
};